import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type {
  CompanyDossier,
  EmployerFootprintAdminViewDto,
  EmployerVerificationDetail,
  PersistedGateCheckpointDto,
} from '../../api/employerVerificationDashboardApi';
import {
  filterPublishedCheckpointsForGate,
  gateMeta,
  GATE_ORDER_PIPE,
  getGateEvidencePolicy,
  type GateKey,
} from '../../lib/employerVerificationCopy';
import {
  formatGateStepLabel,
  gateDisplayBucketFromStatus,
  rollupGateDisplayBucket,
  resolvedCheckpointDisplayStatus,
  type GateDisplayBucket,
} from '../../lib/employerVerificationGateAudit';
import { formatGateReasonReadableFlow } from '../../lib/employerVerificationGateReasonFormat';
import {
  comparedValuesForCheckpointStep,
  type ComparedValueRow,
} from '../../lib/checkpointComparedValues';
import { AdjudicatorDataPointsPanel } from '../employerReview/AdjudicatorDataPointsPanel';
import {
  buildLegitimacyCheckpointView,
  isLegitimacyCheckpoint,
} from '../../lib/employerReview/legitimacyCheckpointPresentation';
import {
  buildLinkageCheckpointView,
  isLinkageCheckpoint,
} from '../../lib/employerReview/linkageCheckpointPresentation';
import {
  buildStructuralSanityCheckpointView,
  isStructuralSanityCheckpoint,
} from '../../lib/employerReview/structuralSanityCheckpointPresentation';
import {
  buildGateSkippedNotice,
  parseGateCheckpointAudit,
} from './EmployerInvestigationGateSupport';
import {
  checkpointTourTarget,
  EMPLOYER_GATE_ANALYSIS_FIRST_REVIEW_TARGET,
} from '../../lib/employerReview/checkpointTourCopy';
import { CheckpointPopoverGuide } from '../employerReview/guidedTour/CheckpointPopoverGuide';
import { useEmployerEvidenceTourOptional } from '../employerReview/guidedTour/EmployerEvidenceTourContext';

type GateBucket = GateDisplayBucket;

type GateAnalysisRow = {
  id: string;
  gate: GateKey;
  gateTitle: string;
  bucket: GateBucket;
  step: string;
  checkName: string;
  hardStop: boolean;
  message: string;
  compared: ComparedValueRow[];
};

type GateAnalysisTable = {
  gate: GateKey;
  gateTitle: string;
  gateBucket: GateBucket;
  rows: GateAnalysisRow[];
};

function stepBucket(cp: PersistedGateCheckpointDto): GateBucket {
  return gateDisplayBucketFromStatus(resolvedCheckpointDisplayStatus(cp));
}

function joinTitles(titles: string[]): string {
  if (titles.length === 0) return '';
  if (titles.length === 1) return titles[0]!;
  if (titles.length === 2) return `${titles[0]} and ${titles[1]}`;
  return `${titles.slice(0, -1).join(', ')}, and ${titles[titles.length - 1]}`;
}

function buildPlainEnglishSummary(buckets: Record<GateBucket, GateKey[]>): {
  headline: string;
  lines: string[];
} {
  const fail = buckets.fail.map((g) => gateMeta[g].title);
  const review = buckets.review.map((g) => gateMeta[g].title);
  const pass = buckets.pass.map((g) => gateMeta[g].title);
  const unavailable = buckets.unavailable.map((g) => gateMeta[g].title);
  const ran = [...fail, ...review, ...pass];
  const total = GATE_ORDER_PIPE.length;

  const lines: string[] = [];
  if (ran.length === total && fail.length === 0 && review.length === 0) {
    return {
      headline: `We checked ${total} areas — all passed.`,
      lines: [`Passed: ${joinTitles(pass)}.`],
    };
  }

  if (ran.length === 0) {
    return {
      headline: 'Formal gate checks have not produced results yet.',
      lines: unavailable.length
        ? [`Not available: ${joinTitles(unavailable)}.`]
        : ['No gate outcomes are on file for this application.'],
    };
  }

  let headline = `We checked ${ran.length} of ${total} areas.`;
  if (fail.length) headline = `${headline} ${fail.length === 1 ? 'One check failed' : `${fail.length} checks failed`}.`;
  else if (review.length)
    headline = `${headline} ${review.length === 1 ? 'One check needs review' : `${review.length} checks need review`}.`;
  else headline = `${headline} The checks that ran passed.`;

  if (fail.length) lines.push(`Failed: ${joinTitles(fail)}.`);
  if (review.length) lines.push(`Needs review: ${joinTitles(review)}.`);
  if (pass.length) lines.push(`Passed: ${joinTitles(pass)}.`);
  if (unavailable.length) lines.push(`Not available: ${joinTitles(unavailable)}.`);

  return { headline, lines };
}

function StatusIcon({ bucket }: { bucket: GateBucket }) {
  if (bucket === 'fail') return <CancelRoundedIcon sx={{ fontSize: 18, color: 'error.main' }} />;
  if (bucket === 'review') return <WarningAmberRoundedIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
  if (bucket === 'pass') return <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'success.main' }} />;
  return <HelpOutlineRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />;
}

function statusLabel(bucket: GateBucket): string {
  if (bucket === 'fail') return 'Fail';
  if (bucket === 'review') return 'Review';
  if (bucket === 'pass') return 'Pass';
  return 'N/A';
}

function CellLabel({ children }: { children: string }) {
  return (
    <Typography
      component="span"
      sx={{
        display: { xs: 'inline', sm: 'none' },
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        mr: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function CheckpointAnalysisRow({
  row,
  cols,
  detail,
  footprint,
  isFirstReviewTarget = false,
}: {
  row: GateAnalysisRow;
  cols: Record<string, unknown>;
  detail: EmployerVerificationDetail;
  footprint?: EmployerFootprintAdminViewDto | null;
  isFirstReviewTarget?: boolean;
}) {
  const theme = useTheme();
  const tour = useEmployerEvidenceTourOptional();
  const rowRef = useRef<HTMLDivElement>(null);
  const checkpointId = `${row.gate}:${row.step}`;
  const isGuided = tour?.activeCheckpointId === checkpointId;

  return (
    <Box
      ref={rowRef}
      data-tour-target={checkpointTourTarget(row.gate, row.step)}
      sx={{
        ...cols,
        px: 1.25,
        py: 0.45,
        position: 'relative',
        bgcolor:
          theme.palette.mode === 'light'
            ? alpha(theme.palette.common.black, isGuided ? 0.03 : 0.01)
            : alpha(theme.palette.common.white, isGuided ? 0.06 : 0.02),
        outline: isGuided ? `2px solid ${alpha(theme.palette.primary.main, 0.75)}` : 'none',
        outlineOffset: -1,
        borderRadius: isGuided ? 1 : 0,
        '&:hover': {
          bgcolor:
            theme.palette.mode === 'light'
              ? alpha(theme.palette.common.black, 0.025)
              : alpha(theme.palette.common.white, 0.04),
        },
      }}
    >
      <Box sx={{ minWidth: 0, pr: 0.5 }}>
        <CellLabel>Check</CellLabel>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {row.checkName}
          {row.hardStop ? (
            <Typography
              component="span"
              variant="caption"
              color="error"
              sx={{ ml: 0.75, fontWeight: 700 }}
            >
              Hard stop
            </Typography>
          ) : null}
        </Typography>
      </Box>

      <Box>
        <CellLabel>Status</CellLabel>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <StatusIcon bucket={row.bucket} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              display: { xs: 'inline', sm: 'none' },
              color: 'text.secondary',
            }}
          >
            {statusLabel(row.bucket)}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ justifySelf: 'center' }}>
        <CellLabel>Review</CellLabel>
        <MessageInfoButton
          gate={row.gate}
          step={row.step}
          checkName={row.checkName}
          message={row.message}
          compared={row.compared}
          detail={detail}
          footprint={footprint}
          bucket={row.bucket}
          rowAnchorRef={rowRef}
          tourTarget={isFirstReviewTarget ? EMPLOYER_GATE_ANALYSIS_FIRST_REVIEW_TARGET : undefined}
        />
      </Box>
    </Box>
  );
}

function MessageInfoButton({
  gate,
  step,
  checkName,
  message,
  compared,
  detail,
  footprint = null,
  bucket,
  rowAnchorRef,
  tourTarget,
}: {
  gate: GateKey;
  step: string;
  checkName: string;
  message: string;
  compared: ComparedValueRow[];
  detail: EmployerVerificationDetail;
  footprint?: EmployerFootprintAdminViewDto | null;
  bucket: GateBucket;
  rowAnchorRef?: RefObject<HTMLElement | null>;
  tourTarget?: string;
}) {
  const theme = useTheme();
  const tour = useEmployerEvidenceTourOptional();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const checkpointId = `${gate}:${step}`;
  const isGuidedStep =
    tour?.activeCheckpointId === checkpointId && tour.checkpointDetailsOpen;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = isGuidedStep || Boolean(anchor);

  useEffect(() => {
    if (isGuidedStep) {
      setAnchor(rowAnchorRef?.current ?? buttonRef.current);
      return;
    }
    if (tour?.activeCheckpointId === checkpointId) {
      setAnchor(null);
    }
  }, [isGuidedStep, tour?.activeCheckpointId, checkpointId, rowAnchorRef]);
  const adjudicatorView = isStructuralSanityCheckpoint(step)
    ? buildStructuralSanityCheckpointView({
        step,
        reason: message,
        bucket,
        detail,
        footprint,
      })
    : isLegitimacyCheckpoint(step)
      ? buildLegitimacyCheckpointView({
          step,
          reason: message,
          bucket,
          detail,
          footprint,
        })
      : isLinkageCheckpoint(step)
        ? buildLinkageCheckpointView({
            step,
            reason: message,
            bucket,
            detail,
            footprint,
          })
        : null;
  const hasDetail =
    adjudicatorView != null
      ? adjudicatorView.dataPoints.length > 0 || Boolean(adjudicatorView.issueStatement)
      : Boolean(message.trim()) || compared.length > 0;

  const issueNote =
    adjudicatorView?.issueStatement?.trim() ||
    message.trim() ||
    undefined;

  const activeTourStep =
    isGuidedStep && tour?.activeTour
      ? tour.activeTour.steps[tour.activeTour.stepIndex]
      : undefined;
  const guideIntro =
    activeTourStep && 'description' in activeTourStep
      ? activeTourStep.description
      : 'Review this checkpoint against the declared employment.';
  const guidePoints =
    activeTourStep && 'lookFor' in activeTourStep && activeTourStep.lookFor?.length
      ? activeTourStep.lookFor
      : [
          'Compare declared values with registry, bank, and map signals.',
          'Confirm the result fits policy for this concern.',
          'Note anything that needs follow-up or override.',
        ];

  const guideIsRedFlag =
    bucket === 'fail' ||
    adjudicatorView?.issueSeverity === 'error' ||
    /exceeds|attention:|below threshold|no google places|could not|contradict/i.test(issueNote ?? '');

  if (!hasDetail) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }

  const guided = isGuidedStep && tour?.activeTour;

  return (
    <>
      <IconButton
        ref={buttonRef}
        size="small"
        aria-label={`Details for ${checkName}`}
        aria-expanded={open}
        onClick={(e) => setAnchor(e.currentTarget)}
        data-tour-target={tourTarget}
        sx={{
          color: isGuidedStep ? 'primary.main' : 'text.secondary',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => {
          if (isGuidedStep) {
            tour?.closeTour();
            return;
          }
          setAnchor(null);
        }}
        anchorOrigin={
          guided
            ? { vertical: 'center', horizontal: 'right' }
            : { vertical: 'bottom', horizontal: 'right' }
        }
        transformOrigin={
          guided
            ? { vertical: 'center', horizontal: 'left' }
            : { vertical: 'top', horizontal: 'right' }
        }
        slotProps={{
          paper: {
            sx: {
              maxWidth:
                adjudicatorView &&
                'secondaryDataPoints' in adjudicatorView &&
                Array.isArray(adjudicatorView.secondaryDataPoints) &&
                adjudicatorView.secondaryDataPoints.length > 0
                  ? 520
                  : 440,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: isGuidedStep
                ? guideIsRedFlag
                  ? 'error.main'
                  : 'warning.dark'
                : 'divider',
              boxShadow: isGuidedStep
                ? theme.palette.mode === 'light'
                  ? '0 16px 48px rgba(15, 23, 42, 0.18)'
                  : '0 16px 48px rgba(0, 0, 0, 0.5)'
                : undefined,
              zIndex: 1450,
              ml: guided ? 1 : 0,
            },
          },
        }}
      >
        {guided ? (
          <CheckpointPopoverGuide
            findingTitle={tour.activeFindingTitle ?? 'Review this check'}
            guideIntro={guideIntro}
            guidePoints={guidePoints}
            issueNote={issueNote}
            stepIndex={tour.activeTour!.stepIndex}
            totalSteps={tour.activeTour!.steps.length}
            isRedFlag={guideIsRedFlag}
            onBack={() => tour.prevStep()}
            onNext={() => {
              if (tour.activeTour!.stepIndex >= tour.activeTour!.steps.length - 1) {
                tour.closeTour();
              } else {
                tour.nextStep();
              }
            }}
            onClose={() => tour.closeTour()}
          />
        ) : null}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75, lineHeight: 1.35 }}>
          {adjudicatorView?.popoverTitle ?? checkName}
        </Typography>
        {adjudicatorView ? (
          <AdjudicatorDataPointsPanel
            dataPoints={adjudicatorView.dataPoints}
            secondaryDataPoints={
              'secondaryDataPoints' in adjudicatorView &&
              Array.isArray(adjudicatorView.secondaryDataPoints)
                ? adjudicatorView.secondaryDataPoints
                : undefined
            }
            secondarySectionLabel={
              'secondarySectionLabel' in adjudicatorView &&
              typeof adjudicatorView.secondarySectionLabel === 'string'
                ? adjudicatorView.secondarySectionLabel
                : undefined
            }
            issueStatement={adjudicatorView.issueStatement}
            issueSeverity={adjudicatorView.issueSeverity}
            issueLabel={adjudicatorView.issueLabel}
            footerNote={adjudicatorView.footerNote}
            advisoryLabel={adjudicatorView.advisoryLabel}
            advisorySeverity={adjudicatorView.advisorySeverity}
          />
        ) : (
          <>
            {message.trim() ? (
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ lineHeight: 1.55, whiteSpace: 'pre-line', wordBreak: 'break-word' }}
              >
                {message}
              </Typography>
            ) : null}
            {compared.length > 0 ? (
              <Stack spacing={0.35} sx={{ mt: message.trim() ? 1 : 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: 0.02, textTransform: 'uppercase' }}
                >
                  Compared
                </Typography>
                {compared.map((row) => (
                  <Typography
                    key={`${checkName}-${row.label}`}
                    variant="body2"
                    sx={{ lineHeight: 1.45, wordBreak: 'break-word' }}
                  >
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {row.label}:
                    </Box>{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {row.value}
                    </Box>
                  </Typography>
                ))}
              </Stack>
            ) : null}
          </>
        )}
      </Popover>
    </>
  );
}

function buildAnalysisTables(
  gates: Record<string, string | null>,
  audit: Record<string, PersistedGateCheckpointDto[]> | null,
  detail: EmployerVerificationDetail,
): GateAnalysisTable[] {
  return GATE_ORDER_PIPE.map((gate) => {
    const raw = typeof gates[gate] === 'string' ? gates[gate] : null;
    const steps = audit?.[gate] ?? [];
    const gateStatus = rollupGateDisplayBucket(raw, steps);
    const policy = getGateEvidencePolicy(gate);
    const published = filterPublishedCheckpointsForGate(detail.checkpoints, gate);
    const skippedNotice = buildGateSkippedNotice({
      gateTitle: gateMeta[gate].title,
      gateStatusRaw: raw,
      auditSteps: steps,
      published,
    });
    const gateTitle = gateMeta[gate].title;
    const rows: GateAnalysisRow[] = [];

    if (gateStatus === 'unavailable' || steps.length === 0) {
      rows.push({
        id: `${gate}:summary`,
        gate,
        gateTitle,
        bucket: gateStatus,
        step: gate,
        checkName: gateTitle,
        hardStop: false,
        message:
          gateStatus === 'unavailable'
            ? skippedNotice ??
              (policy.evaluationLead
                ? `Not run — ${policy.evaluationLead}`
                : 'No evaluation output for this check.')
            : gateMeta[gate].line,
        compared: [],
      });
    } else {
      for (const step of steps) {
        const fullReason = formatGateReasonReadableFlow(step.reason).trim() || '—';
        rows.push({
          id: `${gate}:${step.step}`,
          gate,
          gateTitle,
          bucket: stepBucket(step),
          step: step.step,
          checkName: formatGateStepLabel(step.step),
          hardStop: Boolean(step.isHardStop),
          message: fullReason,
          compared: comparedValuesForCheckpointStep(step.step, detail.evidence),
        });
      }
    }

    return { gate, gateTitle, gateBucket: gateStatus, rows };
  });
}

export function EmployerInvestigationBody({
  detail,
  dossier: _dossier = null,
  footprint = null,
  selectedGate: _controlledGate,
  onSelectedGateChange: _onSelectedGateChange,
}: {
  detail: EmployerVerificationDetail;
  dossier?: CompanyDossier | null;
  footprint?: EmployerFootprintAdminViewDto | null;
  /** Kept for workspace URL compat; gate tabs removed — outcomes are listed by status. */
  selectedGate?: GateKey;
  onSelectedGateChange?: (gate: GateKey) => void;
}) {
  const theme = useTheme();
  const gates = useMemo(
    () => (detail.gates ?? {}) as Record<string, string | null>,
    [detail.gates],
  );

  const audit = useMemo(
    () => parseGateCheckpointAudit(detail.gateCheckpointAudit),
    [detail.gateCheckpointAudit],
  );

  const buckets = useMemo(() => {
    const next: Record<GateBucket, GateKey[]> = {
      fail: [],
      review: [],
      unavailable: [],
      pass: [],
    };
    for (const gate of GATE_ORDER_PIPE) {
      const raw = typeof gates[gate] === 'string' ? gates[gate] : null;
      const steps = audit?.[gate] ?? [];
      next[rollupGateDisplayBucket(raw, steps)].push(gate);
    }
    return next;
  }, [gates, audit]);

  const summary = useMemo(() => buildPlainEnglishSummary(buckets), [buckets]);
  const tables = useMemo(() => buildAnalysisTables(gates, audit, detail), [gates, audit, detail]);
  const firstReviewRowId = useMemo(() => {
    for (const table of tables) {
      for (const row of table.rows) {
        if (row.bucket === 'review' || row.bucket === 'fail') return row.id;
      }
    }
    return null;
  }, [tables]);

  /** Within each gate card: Check fills leftover width; Status + Review stay packed. */
  const cols = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 3.5rem 2.5rem',
    columnGap: 1,
    alignItems: 'center',
    width: '100%',
  } as const;

  return (
    <Stack spacing={1.25}>
      <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, py: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          {summary.headline}
        </Typography>
        {summary.lines.length > 0 ? (
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {summary.lines.map((line) => (
              <Typography key={line} variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {line}
              </Typography>
            ))}
          </Stack>
        ) : null}
      </Alert>

      {tables.length === 0 ? (
        <Box
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            px: 1.25,
            py: 1.5,
            bgcolor: 'background.paper',
            width: '100%',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No gate checkpoint rows to show for this run.
          </Typography>
        </Box>
      ) : (
        <Box
          data-testid="employer-gate-analysis-grid"
          sx={{
            display: 'grid',
            width: '100%',
            gap: 1.25,
            alignItems: 'stretch',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(260px, 1fr))',
              md: 'repeat(auto-fill, minmax(280px, 1fr))',
              lg: 'repeat(auto-fill, minmax(300px, 1fr))',
            },
          }}
        >
          {tables.map((table) => (
            <Box
              key={table.gate}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                width: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
              data-testid={`employer-gate-analysis-table-${table.gate.toLowerCase()}`}
            >
              <Box
                sx={{
                  px: 1.25,
                  py: 0.55,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  bgcolor:
                    theme.palette.mode === 'light'
                      ? alpha(theme.palette.primary.main, 0.04)
                      : alpha(theme.palette.common.white, 0.04),
                }}
              >
                <StatusIcon bucket={table.gateBucket} />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800, lineHeight: 1.25, fontSize: '0.875rem', minWidth: 0 }}
                >
                  {table.gateTitle}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    ml: 'auto',
                    flexShrink: 0,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {statusLabel(table.gateBucket)}
                </Typography>
              </Box>

              <Box
                sx={{
                  ...cols,
                  px: 1.25,
                  py: 0.35,
                  display: { xs: 'none', sm: 'grid' },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor:
                    theme.palette.mode === 'light'
                      ? alpha(theme.palette.common.black, 0.015)
                      : alpha(theme.palette.common.white, 0.03),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  Check
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  Status
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textAlign: 'center',
                  }}
                >
                  Review
                </Typography>
              </Box>

              <Stack
                divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}
                sx={{ flex: 1 }}
              >
                {table.rows.map((row) => (
                  <CheckpointAnalysisRow
                    key={row.id}
                    row={row}
                    cols={cols}
                    detail={detail}
                    footprint={footprint}
                    isFirstReviewTarget={row.id === firstReviewRowId}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
}
